import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { HttpError, createQueryKeys } from "@nasam/kernel";
import type {
  CreatePoiInput,
  PoiBulkRow,
  PoiExportQuery,
  PoisRepository,
  UpdatePoiInput,
} from "../domain";
import type { BulkPreviewOutcome } from "../../../shared/bulkUpload";
import { requestError } from "../../../shared/utils/requestError";
import { saveFile } from "../../../shared/utils/saveFile";

export const poisQueryKeys = createQueryKeys("fleet-pois");

/** The list loads this many POIs at a time, the next as it's scrolled to. */
export const POI_PAGE_SIZE = 10;

/** The POI list, page after page (`GET /pois`); `search` is matched by the
 *  API against the name. */
export function usePoisListQuery(repository: PoisRepository, search: string) {
  return useInfiniteQuery({
    queryKey: poisQueryKeys.list({ search }),
    queryFn: ({ pageParam }) =>
      repository.list({ page: pageParam, limit: POI_PAGE_SIZE, search }),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.meta.nextPageUrl === null ? undefined : last.meta.currentPage + 1,
  });
}

/** Every POI the search matches, for the map layer (`GET /pois/map`). */
export function usePoisMapQuery(repository: PoisRepository, search: string) {
  return useQuery({
    queryKey: [...poisQueryKeys.all, "map", { search }],
    queryFn: () => repository.listForMap({ search }),
  });
}

/** One POI, for its details screen (`GET /pois/:poiId`). */
export function usePoiQuery(repository: PoisRepository, id: string) {
  return useQuery({
    queryKey: poisQueryKeys.detail(id),
    queryFn: () => repository.getById(id),
  });
}

export function useCreatePoiMutation(
  repository: PoisRepository,
  failureMessage: string,
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreatePoiInput) => {
      try {
        return await repository.create(input);
      } catch (error) {
        // The app-wide mutation error toast shows the message; the form keeps
        // its values for a fix or a retry.
        throw requestError(error, failureMessage);
      }
    },
    // The POI list and its map layer load the new POI.
    onSuccess: () => client.invalidateQueries({ queryKey: poisQueryKeys.all }),
  });
}

export function useUpdatePoiMutation(
  repository: PoisRepository,
  failureMessage: string,
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      input,
    }: {
      readonly id: string;
      readonly input: UpdatePoiInput;
    }) => {
      try {
        return await repository.update(id, input);
      } catch (error) {
        // Shown by the app-wide mutation error toast ("POI already
        // exists."); the form keeps its values for a fix or a retry.
        throw requestError(error, failureMessage);
      }
    },
    // The list, the map layer and the details show the change.
    onSuccess: () => client.invalidateQueries({ queryKey: poisQueryKeys.all }),
  });
}

export function useDeletePoiMutation(
  repository: PoisRepository,
  failureMessage: string,
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      try {
        return await repository.delete(id);
      } catch (error) {
        // Shown by the app-wide mutation error toast; the dialog stays open
        // for a retry.
        throw requestError(error, failureMessage);
      }
    },
    // The POI leaves the list and the map.
    onSuccess: () => client.invalidateQueries({ queryKey: poisQueryKeys.all }),
  });
}

/** The name the API gives the template (its `Content-Disposition`). */
export const POI_TEMPLATE_FILE_NAME = "poi-bulk-upload-template.xlsx";

/** Download (Bulk Upload's template): fetches it with the user's token and
 *  saves it. */
export function useDownloadPoiTemplateMutation(
  repository: PoisRepository,
  failureMessage: string,
) {
  return useMutation({
    mutationFn: async () => {
      let file: Blob;
      try {
        file = await repository.downloadTemplate();
      } catch (error) {
        // A file request's error body is a file too, so the server's message
        // can't be read: always `failureMessage`, in the app-wide toast.
        throw new Error(failureMessage, { cause: error });
      }
      saveFile(file, POI_TEMPLATE_FILE_NAME);
    },
  });
}

/** The name the API gives the export (its `Content-Disposition`). */
export const POI_EXPORT_FILE_NAME = "pois.xlsx";

/** Download on the POI list: every POI it shows, as an Excel file, fetched
 *  with the user's token and saved. */
export function useExportPoisMutation(
  repository: PoisRepository,
  failureMessage: string,
) {
  return useMutation({
    mutationFn: async (query: PoiExportQuery) => {
      let file: Blob;
      try {
        file = await repository.exportPois(query);
      } catch (error) {
        // As the template's: the error body is a file, so its message can't
        // be read.
        throw new Error(failureMessage, { cause: error });
      }
      saveFile(file, POI_EXPORT_FILE_NAME);
    },
  });
}

export type PoiBulkPreviewOutcome = BulkPreviewOutcome<PoiBulkRow>;

/** Check (Bulk Upload): sends the file to be read and validated; nothing is
 *  created. A refusal comes back as an outcome, not an error, so it shows on
 *  the form instead of in the app-wide toast. */
export function usePreviewPoiBulkUploadMutation(
  repository: PoisRepository,
  failureMessage: string,
) {
  return useMutation({
    mutationFn: async (file: File): Promise<PoiBulkPreviewOutcome> => {
      try {
        return {
          kind: "checked",
          preview: await repository.previewBulkUpload(file),
        };
      } catch (error) {
        const explained = requestError(error, failureMessage);
        if (explained instanceof HttpError) {
          return { kind: "refused", message: explained.message };
        }
        throw explained;
      }
    },
  });
}

/** Confirm (Bulk Upload): imports the same file's valid rows. */
export function useBulkUploadPoisMutation(
  repository: PoisRepository,
  failureMessage: string,
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      try {
        return await repository.bulkUpload(file);
      } catch (error) {
        // Shown by the app-wide mutation error toast ("None of the records in
        // the file can be imported."); the dialog stays open.
        throw requestError(error, failureMessage);
      }
    },
    // The new POIs join the list and the map layer.
    onSuccess: () => client.invalidateQueries({ queryKey: poisQueryKeys.all }),
  });
}

/** The name the API gives the error report (its `Content-Disposition`). */
export const POI_BULK_ERROR_REPORT_FILE_NAME = "poi-bulk-upload-errors.xlsx";

/** Download Error Report (Bulk Upload, Scenarios 13 and 14): sends the same
 *  file again -- the API keeps nothing between calls -- and saves its
 *  refused rows. */
export function useDownloadPoiBulkErrorReportMutation(
  repository: PoisRepository,
  failureMessage: string,
) {
  return useMutation({
    mutationFn: async (file: File) => {
      let report: Blob;
      try {
        report = await repository.downloadBulkErrorReport(file);
      } catch (error) {
        // As the template's: the error body is a file too, so always
        // `failureMessage`, in the app-wide toast.
        throw new Error(failureMessage, { cause: error });
      }
      saveFile(report, POI_BULK_ERROR_REPORT_FILE_NAME);
    },
  });
}
